import {FormSection} from '@/components/commons/form-section/FormSection'
import {Switch} from '@/components/commons/switch/Switch'
import {WarningBanner} from '@/components/commons/warning-banner/WarningBanner'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {Grid, GridItem} from '@/components/ui/grid-atoms/GridAtoms'
import {ProjectFormEndDateField} from '@/features/projects/components/project-form/project-form-end-date-field/ProjectFormEndDateField'
import {ProjectFormSourceField} from '@/features/projects/components/project-form/project-form-source-field/ProjectFormSourceField'
import {ProjectFieldsSchema} from '@/features/projects/formSchemas'
import {ProjectEditingOperation, ProjectType} from '@/features/projects/types'
import {useTranslation} from '@/translations/i18n'
import {Controller, useFormContext, useWatch} from 'react-hook-form'
import {z} from 'zod'
import {ProjectFormEndingReasonField} from '../project-form-ending-reason-field/ProjectFormEndingReasonField'

const ProjectFormReferenceSectionSchema = ProjectFieldsSchema.pick({
    hasReopening: true,
    endDate: true,
    endingReason: true
})
type ProjectFormReferenceSectionSchema = z.infer<typeof ProjectFormReferenceSectionSchema>

export const ProjectFormReferenceSection = ({
    isAllowed,
    editingOperation,
    projectType
}: {
    isAllowed: boolean
    editingOperation: ProjectEditingOperation
    projectType?: ProjectType | null
}) => {
    const {t} = useTranslation()
    const {control} = useFormContext<ProjectFormReferenceSectionSchema>()
    const endDate = useWatch({control, name: 'endDate'})
    const hasReopening = useWatch({control, name: 'hasReopening'})

    const renderVariationFields = () => (
        <Grid columns={3} columnGap={4} fullWidth rowGap={4}>
            <GridItem spanColumn={1} fullWidth>
                <ProjectFormEndDateField />
            </GridItem>

            {!hasReopening && (
                <GridItem spanColumn={2} fullWidth>
                    <ProjectFormEndingReasonField />
                </GridItem>
            )}

            <GridItem spanColumn={3} fullWidth>
                <Controller
                    control={control}
                    name="hasReopening"
                    render={({field}) => (
                        <Switch
                            size="sm"
                            label={t('projects:reopen_project_with_variations')}
                            defaultChecked={field.value}
                            disabled={!endDate}
                            onChange={value => field.onChange(value)}
                        />
                    )}
                />
            </GridItem>
        </Grid>
    )

    const renderWarningBanner = {
        renew: {
            cyclic: (
                <WarningBanner
                    title={t('projects:extends_project_error_title')}
                    subtitle={t('projects:extends_project_error_description')}
                />
            ),
            continuous: (
                <WarningBanner
                    title={t('projects:renew_project_error_title')}
                    subtitle={t('projects:renew_project_error_description')}
                />
            ),
            default: (
                <WarningBanner
                    title={t('projects:renew_project_error_default_title')}
                    subtitle={t('projects:renew_project_error_default_description')}
                />
            )
        },
        transform: (
            <WarningBanner
                title={t('projects:transform_project_error_title')}
                subtitle={t('projects:transform_project_error_description')}
            />
        ),
        variation: (
            <WarningBanner
                title={t('projects:variation_project_error_title')}
                subtitle={t('projects:variation_project_error_description')}
            />
        ),
        resume: (
            <WarningBanner
                title={t('projects:resume_project_error_title')}
                subtitle={t('projects:resume_project_error_description')}
            />
        )
    }

    return (
        <FormSection title={t('commons:reference_project')}>
            {isAllowed ? (
                <Flexbox direction="column" gap={4}>
                    <ProjectFormSourceField />
                    {editingOperation == 'transform' && <></>}
                    {(!editingOperation || editingOperation == 'variation') && renderVariationFields()}
                </Flexbox>
            ) : editingOperation == 'renew' ? (
                projectType ? (
                    renderWarningBanner.renew[projectType]
                ) : (
                    renderWarningBanner.renew.default
                )
            ) : (
                renderWarningBanner[editingOperation]
            )}
        </FormSection>
    )
}
